import { createSelector } from '@reduxjs/toolkit';

import { ProductTypeCodes } from 'entities/progress';
import { CountryISOCodes } from 'common/countries';
import { RootState } from 'state/store';

export const getRoot = (state: RootState) => state.progress;
export const getProfile = (state: RootState) => state.profile;

// Select only active products and filter out SCF for non-USA businesses or if Capital active and filter out Capital if not active
export const getBusinessProducts = createSelector(
  [getRoot, getProfile],
  ({ businessProducts }, { businessAddress }) => {
    const hasActiveCapital = businessProducts.some(
      (p) => p.isActive && p.productsTypeCode === ProductTypeCodes.capital,
    );

    return businessProducts.filter(
      (prod) =>
        prod.productsIsActive &&
        (businessAddress.countryIsocode === CountryISOCodes.USA ||
          prod.productsTypeCode !== ProductTypeCodes.scf) &&
        (prod.isActive || prod.productsTypeCode !== ProductTypeCodes.capital) &&
        !(hasActiveCapital && prod.productsTypeCode === ProductTypeCodes.scf),
    );
  },
);

export const getIsCapitalProductActive = createSelector(
  [getRoot],
  ({ isCapitalProductActive }) => isCapitalProductActive,
);

export const getNewProductType = createSelector(
  [getRoot],
  ({ newProductType }) => newProductType,
);

export const getActiveProductsTypeCodes = createSelector(
  [getBusinessProducts],
  (products) =>
    products
      .filter((product) => product.isActive)
      .map((product) => product.productsTypeCode),
);

export const getIsCapitalOnlyProduct = createSelector(
  [getActiveProductsTypeCodes],
  (products) =>
    products.length === 1 && products.includes(ProductTypeCodes.capital),
);

export const getProcent = createSelector(
  [getRoot, getActiveProductsTypeCodes],
  ({ procentByTab }, products) => {
    const { owner, businessInfo, financial, transactional, businessPlan } =
      procentByTab;

    const tabs: any = [];

    if (
      products.includes(ProductTypeCodes.payments) ||
      products.includes(ProductTypeCodes.capital)
    ) {
      tabs.push(businessInfo.progress);
    }

    if (products.includes(ProductTypeCodes.payments)) {
      tabs.push(owner.progress);
    }

    if (products.includes(ProductTypeCodes.services)) {
      tabs.push(...[financial.progress, businessPlan.progress]);
    }

    if (products.includes(ProductTypeCodes.crossBorderPayments)) {
      tabs.push(transactional.progress);
    }

    if (tabs.length > 0) {
      return tabs.reduce((sum, a) => sum + a, 0) / tabs.length;
    }

    return null;
  },
);

export const getOwnershipProcent = createSelector(
  [getRoot, getActiveProductsTypeCodes],
  ({ procentByTab }, products) =>
    products.includes(ProductTypeCodes.payments)
      ? procentByTab.owner.progress
      : null,
);

export const getProcentExcludingOwner = createSelector(
  [getRoot, getActiveProductsTypeCodes],
  ({ procentByTab }, products) => {
    const { businessInfo, financial, transactional, businessPlan } =
      procentByTab;

    const tabs: any = [];

    if (
      products.includes(ProductTypeCodes.payments) ||
      products.includes(ProductTypeCodes.capital)
    ) {
      tabs.push(businessInfo.progress);
    }

    if (products.includes(ProductTypeCodes.services)) {
      tabs.push(...[financial.progress, businessPlan.progress]);
    }

    if (products.includes(ProductTypeCodes.crossBorderPayments)) {
      tabs.push(transactional.progress);
    }

    if (tabs.length > 0) {
      return tabs.reduce((sum, a) => sum + a, 0) / tabs.length;
    }

    return null;
  },
);

export const getTabProgresses = createSelector(
  [getRoot],
  ({ procentByTab }) => procentByTab,
);

export const getShowErrors = createSelector(
  [getRoot],
  ({ showErrors }) => showErrors,
);
